import * as yup from "yup";
import { InvestmentState } from "@/domain/investment";
import { exceedsAmount, investmentRoom, isWithinProfitabilityCap } from "@/domain/fiscal";
import type { Translator } from "./validationHelpers";

/**
 * ---------------------------------------------------------------------------
 * Investment create/edit form
 * ---------------------------------------------------------------------------
 */

export interface InvestmentValidationMessages {
  required: string;
  amountExceeds: string;
  amountPositive: string;
  returnMax: string;
  returnPositive: string;
}

export interface InvestmentFormContext {
  hasSelectedQuota: boolean;
  deductionAvailable: string | null;
  ownDeduction: string;
}

const INVESTMENT_STATE_VALUES = Object.values(InvestmentState);

export function createInvestmentFormSchema(msgs: InvestmentValidationMessages) {
  return yup.object({
    projectId: yup.string().required(msgs.required),
    quotaId: yup.string().required(msgs.required),
    investorId: yup.string().required(msgs.required),
    amount: yup
      .string()
      .required(msgs.required)
      .test("positive", msgs.amountPositive, (value) => {
        const amount = Number.parseFloat(value ?? "");
        return Number.isNaN(amount) || amount > 0;
      })
      .test("within-available", msgs.amountExceeds, (value, ctx) => {
        const context = ctx.options.context as InvestmentFormContext | undefined;
        if (!context?.hasSelectedQuota || context.deductionAvailable === null) return true;
        const room = investmentRoom(
          context.deductionAvailable,
          String(ctx.parent.returnPercentage ?? ""),
          context.ownDeduction,
        );
        return !exceedsAmount(value ?? "", room);
      }),
    returnPercentage: yup
      .string()
      .required(msgs.required)
      .test("positive", msgs.returnPositive, (value) => {
        const pct = Number.parseFloat(value ?? "");
        return !Number.isNaN(pct) && pct > 0;
      })
      .test("max", msgs.returnMax, (value) => isWithinProfitabilityCap(value)),
    status: yup
      .mixed<InvestmentState>()
      .oneOf(INVESTMENT_STATE_VALUES, msgs.required)
      .required(msgs.required),
  });
}

export type InvestmentFormData = yup.InferType<ReturnType<typeof createInvestmentFormSchema>>;

export function createInvestmentValidationMessages(t: Translator): InvestmentValidationMessages {
  return {
    required: t("validation.required", { defaultValue: "Este campo es obligatorio" }),
    amountExceeds: t("validation.amount_exceeds_available", {
      defaultValue: "Excede el capital disponible",
    }),
    amountPositive: t("validation.amount_not_positive"),
    returnMax: t("validation.return_percentage_max", {
      defaultValue: "Rentabilidad máxima legal 20%",
    }),
    returnPositive: t("validation.return_percentage_positive", {
      defaultValue: "La rentabilidad debe ser mayor que 0",
    }),
  };
}

// Shared schema factory for reservation and financing contracts
export const createSendContractWithDeadlineSchema = (messages: {
  templateRequired: string;
  deadlineRequired: string;
}) =>
  yup.object({
    templateUuid: yup.string().required(messages.templateRequired),
    paymentDeadline: yup.string().required(messages.deadlineRequired),
  });

// Communication contract schema factory — attachments are declared by the
// chosen template, not by the form.
export const createCommunicationContractSchema = (messages: { templateRequired: string }) =>
  yup.object({
    templateUuid: yup.string().required(messages.templateRequired),
  });

// Factory for extend reservation schema (needs dynamic validation)
export const createExtendReservationSchema = (
  messages: { dateRequired: string; datePast: string; dateNotExtended: string },
  currentExpirationDate?: string | null,
) => {
  return yup.object({
    new_expires_at: yup
      .string()
      .required(messages.dateRequired)
      .test("is-future", messages.datePast, (value) => {
        if (!value) return false;
        return new Date(value) > new Date();
      })
      .test("is-after-current", messages.dateNotExtended, (value) => {
        if (!value) return false;
        if (!currentExpirationDate) return true;
        return new Date(value) > new Date(currentExpirationDate);
      }),
  });
};

// Type inference
export type SendContractWithDeadlineFormData = yup.InferType<
  ReturnType<typeof createSendContractWithDeadlineSchema>
>;
export type CommunicationContractFormData = yup.InferType<
  ReturnType<typeof createCommunicationContractSchema>
>;
export type ExtendReservationFormData = yup.InferType<
  ReturnType<typeof createExtendReservationSchema>
>;
